import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Alert,
  Divider,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { useGetMyCompanyQuery, useUpdateCompanyMutation } from '../../services/companyApi';
import { useSelector } from 'react-redux';

const Settings = () => {
  const { user } = useSelector((state) => state.auth);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  const { data: companyData, isLoading, error } = useGetMyCompanyQuery();
  const [updateCompany, { isLoading: updating }] = useUpdateCompanyMutation();

  const company = companyData?.data;

  React.useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        legalName: company.legalName || '',
        code: company.code || '',
        phone: company.phone || '',
        email: company.email || '',
        website: company.website || '',
        address: {
          street: company.address?.street || '',
          city: company.address?.city || '',
          state: company.address?.state || '',
          country: company.address?.country || '',
          zipCode: company.address?.zipCode || '',
        },
        countryCode: company.countryCode || '',
        currency: company.currency || '',
        weeklyOffDays: company.weeklyOffDays || ['Saturday', 'Sunday'],
        employeeIdPrefix: company.employeeIdPrefix || 'EMP',
        settings: {
          attendance: {
            type: company.settings?.attendance?.type || 'system',
            clockInGracePeriod: company.settings?.attendance?.clockInGracePeriod || 15,
            autoClockOut: company.settings?.attendance?.autoClockOut ? 'true' : 'false',
            geoFencing: company.settings?.attendance?.geoFencing ? 'true' : 'false',
            selfieRequired: company.settings?.attendance?.selfieRequired || false,
          },
        },
      });
    }
  }, [company]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const parts = field.split('.');
      setFormData(prev => {
        const updateNested = (obj, keys, val) => {
          if (keys.length === 1) {
            return { ...obj, [keys[0]]: val };
          }
          const [current, ...rest] = keys;
          return {
            ...obj,
            [current]: updateNested(obj[current] || {}, rest, val)
          };
        };
        return updateNested(prev, parts, value);
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Company name is required';
    }
    if (!formData.legalName?.trim()) {
      newErrors.legalName = 'Legal name is required';
    }
    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      await updateCompany({
        id: company._id,
        ...formData
      }).unwrap();

      setEditMode(false);
      // Success message will be shown via RTK Query
    } catch (error) {
      console.error('Update company error:', error);
      setErrors({ submit: error.data?.message || 'Failed to update company' });
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    if (company) {
      setFormData({
        name: company.name || '',
        legalName: company.legalName || '',
        code: company.code || '',
        phone: company.phone || '',
        email: company.email || '',
        website: company.website || '',
        address: {
          street: company.address?.street || '',
          city: company.address?.city || '',
          state: company.address?.state || '',
          country: company.address?.country || '',
          zipCode: company.address?.zipCode || '',
        },
        countryCode: company.countryCode || '',
        currency: company.currency || '',
        weeklyOffDays: company.weeklyOffDays || ['Saturday', 'Sunday'],
        settings: {
          attendance: {
            type: company.settings?.attendance?.type || 'system',
            clockInGracePeriod: company.settings?.attendance?.clockInGracePeriod || 15,
            autoClockOut: company.settings?.attendance?.autoClockOut ? 'true' : 'false',
            geoFencing: company.settings?.attendance?.geoFencing ? 'true' : 'false',
            selfieRequired: company.settings?.attendance?.selfieRequired || false,
          },
        },
      });
    }
    setEditMode(false);
    setErrors({});
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Typography>Loading company settings...</Typography>
      </Box>
    );
  }

  if (error || !company) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error?.data?.message || 'Failed to load company information'}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Company Settings - {company?.name || 'IAR HRMS'}</title>
      </Helmet>

      <Box sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              🏢 Company Settings
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your company information and preferences
            </Typography>
          </Box>

          {!editMode && user?.role === 'company_admin' && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditMode(true)}
              sx={{
                borderRadius: 2,
                px: 3,
                fontWeight: 600,
              }}
            >
              Edit Company
            </Button>
          )}
        </Box>

        {/* Company Overview Card */}
        <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mr: 3,
                  bgcolor: 'primary.main',
                  fontSize: '2rem',
                  fontWeight: 700,
                }}
              >
                {company.name?.charAt(0)?.toUpperCase() || 'C'}
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  {company.name}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                  {company.legalName}
                </Typography>
                <Typography variant="body2" sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  fontWeight: 600,
                }}>
                  <BusinessIcon sx={{ fontSize: 16 }} />
                  {company.code}
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <LocationIcon color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Location</Typography>
                    <Typography variant="body1">
                      {company.address?.city}, {company.address?.country} ({company.countryCode})
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <LanguageIcon color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Currency</Typography>
                    <Typography variant="body1">{company.currency}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <SettingsIcon color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Weekly Off Days</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {(company.weeklyOffDays || ['Saturday', 'Sunday']).map((day) => (
                        <Chip 
                          key={day} 
                          label={day} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Grid>
              {company.phone && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <PhoneIcon color="action" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Phone</Typography>
                      <Typography variant="body1">{company.phone}</Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {company.email && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <EmailIcon color="action" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body1">{company.email}</Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Edit Form Dialog */}
        <Dialog
          open={editMode}
          onClose={handleCancel}
          maxWidth="md"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: 3,
              maxHeight: '90vh',
            },
          }}
        >
          <DialogTitle sx={{
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            fontWeight: 700,
          }}>
            <EditIcon />
            Edit Company Information
          </DialogTitle>

          <DialogContent sx={{ p: 4 }}>
            {errors.submit && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {errors.submit}
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  📋 Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Legal Name"
                  value={formData.legalName || ''}
                  onChange={(e) => handleInputChange('legalName', e.target.value)}
                  error={!!errors.legalName}
                  helperText={errors.legalName}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Code"
                  value={formData.code || ''}
                  onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                  InputProps={{ readOnly: true }}
                  helperText="Company code cannot be changed"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={formData.currency || ''}
                    label="Currency"
                    onChange={(e) => handleInputChange('currency', e.target.value)}
                  >
                    <MenuItem value="USD">USD - US Dollar</MenuItem>
                    <MenuItem value="EUR">EUR - Euro</MenuItem>
                    <MenuItem value="GBP">GBP - British Pound</MenuItem>
                    <MenuItem value="INR">INR - Indian Rupee</MenuItem>
                    <MenuItem value="JPY">JPY - Japanese Yen</MenuItem>
                    <MenuItem value="CAD">CAD - Canadian Dollar</MenuItem>
                    <MenuItem value="AUD">AUD - Australian Dollar</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Contact Information */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  📞 Contact Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Website"
                  value={formData.website || ''}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="https://www.example.com"
                />
              </Grid>

              {/* Address Information */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  📍 Address Information
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={formData.address?.street || ''}
                  onChange={(e) => handleInputChange('address.street', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.address?.city || ''}
                  onChange={(e) => handleInputChange('address.city', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="State/Province"
                  value={formData.address?.state || ''}
                  onChange={(e) => handleInputChange('address.state', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="ZIP/Postal Code"
                  value={formData.address?.zipCode || ''}
                  onChange={(e) => handleInputChange('address.zipCode', e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Country"
                  value={formData.address?.country || ''}
                  onChange={(e) => handleInputChange('address.country', e.target.value)}
                />
              </Grid>

              {/* Business Configuration */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  🏢 Business Configuration
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="weekly-off-days-label">Weekly Off Days</InputLabel>
                  <Select
                    labelId="weekly-off-days-label"
                    multiple
                    value={formData.weeklyOffDays || []}
                    onChange={(e) => handleInputChange('weeklyOffDays', e.target.value)}
                    input={<OutlinedInput label="Weekly Off Days" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                      <MenuItem key={day} value={day}>
                        {day}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Employee ID Prefix"
                  value={formData.employeeIdPrefix || ''}
                  onChange={(e) => handleInputChange('employeeIdPrefix', e.target.value.toUpperCase())}
                  placeholder="EMP"
                  helperText="Prefix for auto-generated employee IDs (e.g., EMP will generate EMP0001, EMP0002, etc.)"
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              {/* Attendance Settings */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  ⏰ Attendance Settings
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Attendance Type</InputLabel>
                  <Select
                    value={formData.settings?.attendance?.type || 'system'}
                    label="Attendance Type"
                    onChange={(e) => handleInputChange('settings.attendance.type', e.target.value)}
                  >
                    <MenuItem value="system">System Clock In/Out</MenuItem>
                    <MenuItem value="selfie">Selfie Required</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Clock In Grace Period (minutes)"
                  type="number"
                  value={formData.settings?.attendance?.clockInGracePeriod || 15}
                  onChange={(e) => handleInputChange('settings.attendance.clockInGracePeriod', parseInt(e.target.value))}
                  inputProps={{ min: 0, max: 60 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Auto Clock Out</InputLabel>
                  <Select
                    value={formData.settings?.attendance?.autoClockOut ? 'true' : 'false'}
                    label="Auto Clock Out"
                    onChange={(e) => handleInputChange('settings.attendance.autoClockOut', e.target.value === 'true')}
                  >
                    <MenuItem value="false">Disabled</MenuItem>
                    <MenuItem value="true">Enabled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Geo Fencing</InputLabel>
                  <Select
                    value={formData.settings?.attendance?.geoFencing ? 'true' : 'false'}
                    label="Geo Fencing"
                    onChange={(e) => handleInputChange('settings.attendance.geoFencing', e.target.value === 'true')}
                  >
                    <MenuItem value="false">Disabled</MenuItem>
                    <MenuItem value="true">Enabled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button
              onClick={handleCancel}
              startIcon={<CancelIcon />}
              sx={{ fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={updating}
              sx={{
                fontWeight: 600,
                px: 4,
              }}
            >
              {updating ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default Settings;
